import type { EChartsOption, TooltipComponentFormatterCallbackParams } from "echarts";
import type { EChartsThemeTokens } from "@/shared/charts/theme/create-echarts-theme";
import type {
  LineAreaChartConfig,
  LineAreaChartPoint,
} from "@/shared/charts/components/line-area-chart/types";
import { formatTimeAxisLabel } from "@/shared/charts/utils/format-time-axis-label";

function formatValue(value: number, config: LineAreaChartConfig): string {
  return config.valueFormatter?.(value) ?? value.toLocaleString("en-GB");
}

function getNiceStep(range: number): number {
  const magnitude = Math.pow(10, Math.floor(Math.log10(range)));
  const normalized = range / magnitude;

  if (normalized <= 1) return magnitude / 10;
  if (normalized <= 2) return magnitude / 5;
  if (normalized <= 5) return magnitude / 2;
  return magnitude;
}

function getMissingDataRanges(
  data: LineAreaChartPoint[]
): [{ xAxis: number }, { xAxis: number }][] {
  let lastObservedIndex = -1;
  data.forEach((point, index) => {
    if (point.value != null) lastObservedIndex = index;
  });
  const dataThroughLastObservation = data.slice(0, lastObservedIndex + 1);
  const ranges: [{ xAxis: number }, { xAxis: number }][] = [];
  let rangeStart: number | null = null;

  for (let index = 0; index <= dataThroughLastObservation.length; index += 1) {
    const isMissing =
      index < dataThroughLastObservation.length &&
      dataThroughLastObservation.at(index)?.value == null;

    if (isMissing && rangeStart == null) {
      rangeStart = index;
    }

    if (!isMissing && rangeStart != null) {
      const startPoint = dataThroughLastObservation.at(rangeStart);
      const endPoint = dataThroughLastObservation.at(index - 1);
      if (startPoint == null || endPoint == null) {
        rangeStart = null;
        continue;
      }

      const startTimestamp = new Date(startPoint.timestamp).getTime();
      const previousTimestamp =
        rangeStart > 0
          ? new Date(
              dataThroughLastObservation.at(rangeStart - 1)?.timestamp ?? startPoint.timestamp
            ).getTime()
          : null;
      const nextPoint = dataThroughLastObservation.at(index);
      const nextTimestamp = nextPoint == null ? null : new Date(nextPoint.timestamp).getTime();
      const leftBoundary =
        previousTimestamp == null
          ? startTimestamp -
            ((nextTimestamp ?? startTimestamp + 60 * 60 * 1000) - startTimestamp) / 2
          : (previousTimestamp + startTimestamp) / 2;
      const endTimestamp = new Date(endPoint.timestamp).getTime();
      const rightBoundary =
        nextTimestamp == null
          ? endTimestamp + (endTimestamp - (previousTimestamp ?? endTimestamp - 60 * 60 * 1000)) / 2
          : (endTimestamp + nextTimestamp) / 2;

      ranges.push([{ xAxis: leftBoundary }, { xAxis: rightBoundary }]);
      rangeStart = null;
    }
  }

  return ranges;
}

function getYAxisMax(data: LineAreaChartPoint[]): number {
  const rawMax = data.reduce((max, point) => Math.max(max, point.value ?? 0), 0);
  if (rawMax === 0) return 10;

  const targetMax = rawMax * 1.15;
  const step = getNiceStep(targetMax);
  return Math.ceil(targetMax / step) * step;
}

function getTooltipDataIndex(params: TooltipComponentFormatterCallbackParams): number {
  const candidate = Array.isArray(params) ? params[0] : params;

  if (candidate != null && typeof candidate === "object" && "dataIndex" in candidate) {
    const dataIndex = (candidate as { dataIndex?: unknown }).dataIndex;
    return typeof dataIndex === "number" ? dataIndex : 0;
  }

  return 0;
}

function formatTooltip(
  params: TooltipComponentFormatterCallbackParams,
  data: LineAreaChartPoint[],
  config: LineAreaChartConfig
): string {
  const point = data[getTooltipDataIndex(params)];
  if (point == null) return "";

  const valueLabel = point.value != null ? formatValue(point.value, config) : "Unavailable";
  return [point.x, `${config.seriesLabel}: ${valueLabel}`].join("\n");
}

export function createLineAreaChartOption(
  data: LineAreaChartPoint[],
  config: LineAreaChartConfig,
  tokens: EChartsThemeTokens
): EChartsOption {
  const seriesColor = config.seriesColor ?? tokens.primarySeriesColor;
  const areaColor = config.areaColor ?? tokens.areaFillColor;
  const missingDataRanges = getMissingDataRanges(data);
  const timeAxisRange =
    config.xAxisRange ??
    (data.length > 0
      ? {
          min: new Date(data[0]?.timestamp ?? 0).getTime(),
          max: new Date(data.at(-1)?.timestamp ?? 0).getTime(),
        }
      : { min: 0, max: 0 });

  return {
    aria: {
      enabled: true,
      label: {
        enabled: true,
        description: config.ariaDescription,
      },
    },
    backgroundColor: tokens.backgroundColor,
    color: [seriesColor],
    grid: {
      top: 44,
      right: 8,
      bottom: 42,
      left: 8,
      containLabel: true,
    },
    legend: {
      top: 8,
      left: "center",
      itemGap: 24,
      itemWidth: 20,
      itemHeight: 2,
      textStyle: {
        color: tokens.mutedTextColor,
        fontFamily: tokens.fontFamily,
        fontSize: 12,
      },
      data: [config.seriesLabel],
    },
    textStyle: {
      color: tokens.textColor,
      fontFamily: tokens.fontFamily,
    },
    tooltip: {
      trigger: "axis",
      renderMode: "richText",
      confine: true,
      backgroundColor: tokens.tooltipBackgroundColor,
      borderColor: tokens.tooltipBorderColor,
      borderWidth: 1,
      padding: [8, 10],
      textStyle: {
        color: tokens.textColor,
        fontFamily: tokens.fontFamily,
        fontSize: 12,
        lineHeight: 18,
      },
      axisPointer: {
        type: "line",
        lineStyle: {
          color: tokens.axisLineColor,
          width: 1,
        },
      },
      formatter: (params: TooltipComponentFormatterCallbackParams) =>
        formatTooltip(params, data, config),
    },
    xAxis: {
      type: "time",
      ...(config.xAxisRange == null ? {} : config.xAxisRange),
      axisLine: {
        lineStyle: {
          color: tokens.axisLineColor,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: tokens.mutedTextColor,
        fontFamily: tokens.fontFamily,
        fontSize: 10,
        margin: 14,
        hideOverlap: true,
        formatter: (value: number) => formatTimeAxisLabel(value, timeAxisRange),
      },
    },
    dataZoom: [
      {
        type: "inside",
        xAxisIndex: 0,
        filterMode: "none",
        zoomLock: true,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: false,
        moveOnMouseMove: true,
      },
    ],
    yAxis: {
      type: "value",
      min: 0,
      max: getYAxisMax(data),
      splitNumber: 4,
      axisLabel: {
        color: tokens.mutedTextColor,
        fontFamily: tokens.fontFamily,
        fontSize: 10,
        formatter: (value: number) => formatValue(value, config),
      },
      splitLine: {
        lineStyle: {
          color: tokens.gridLineColor,
          width: 1,
        },
      },
    },
    series: [
      {
        name: config.seriesLabel,
        type: "line",
        data: data.map((point) => [new Date(point.timestamp).getTime(), point.value]),
        smooth: 0.35,
        showSymbol: false,
        symbol: "circle",
        symbolSize: 6,
        lineStyle: {
          color: seriesColor,
          width: 2.5,
        },
        itemStyle: {
          color: seriesColor,
        },
        areaStyle: {
          color: areaColor,
        },
        ...(missingDataRanges.length > 0
          ? {
              markArea: {
                silent: true,
                itemStyle: { color: tokens.missingDataColor ?? "rgba(0, 0, 0, 0.3)" },
                data: missingDataRanges,
              },
            }
          : {}),
      },
    ],
  };
}
